import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { BarChart3, Scale } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";

const PERIODS = [
  { key: "day", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "halfyear", label: "6 Months" },
  { key: "year", label: "Year" },
  { key: "lifetime", label: "Lifetime" },
];

const GRAM_KEYS = [
  ["24K", "24K"], ["22K", "22K"], ["20K", "20K"], ["18K", "18K"],
  ["silver", "Silver"], ["other", "Other"],
];

const MAROON = "#6B1A2C";
const GOLD = "#C9A063";

/**
 * Sales Analytics — who sold how much, of what.
 *
 * Money and item counts plus GRAMS split by purity, over any period. Scope is
 * enforced by the backend: a salesperson sees only their own figures, an admin
 * sees their reports, major/super admin see everyone.
 */
export default function SalesAnalyticsPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [who, setWho] = useState("");

  useEffect(() => {
    api.get(`/api/crm/sales-analytics?period=${period}`).then((r) => setData(r.data)).catch(() => {});
  }, [period]);

  useEffect(() => {
    const q = who ? `&staff_id=${who}` : "";
    api.get(`/api/crm/sales-analytics/trend?period=${period}${q}`)
      .then((r) => setTrend(r.data?.series || [])).catch(() => setTrend([]));
  }, [period, who]);

  const t = data?.totals || {};
  const grams = t.grams || {};

  const gramChart = GRAM_KEYS
    .map(([k, label]) => ({ name: label, grams: +(grams[k] || 0).toFixed(3) }))
    .filter((r) => r.grams > 0);

  const peopleChart = (data?.people || []).slice(0, 10).map((p) => ({
    name: (p.name || "").split(" ")[0] || "—",
    revenue: p.revenue,
  }));

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Performance</div>
        <h1 className="font-display text-4xl text-maroon">Sales Analytics</h1>
        <p className="text-xs text-ash mt-1">
          {data?.scope === "all" ? "Every seller across both showrooms." : "You and the people reporting to you."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${period === p.key ? "bg-maroon text-pearl border-maroon" : "border-line text-ash hover:border-maroon"}`}
            data-testid={`an-period-${p.key}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Revenue" value={fmtINR(t.revenue || 0)}/>
        <Stat label="Online Orders" value={t.orders_count || 0}/>
        <Stat label="Counter Slips" value={t.slips_count || 0}/>
        <Stat label="Items Sold" value={t.items_sold || 0}/>
        <Stat label="Customisations" value={t.customisations || 0}/>
        <Stat label="Schemes Enrolled" value={t.schemes_enrolled || 0}/>
      </div>

      {/* Gemstone + customisation breakdown */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Gemstones Sold" value={t.gemstones_sold || 0}/>
        <Stat label="Gemstone Revenue" value={fmtINR(t.gemstone_revenue || 0)}/>
        <Stat label="Customisation Revenue" value={fmtINR(t.customisation_revenue || 0)}/>
        <Stat label="Customisation Requests" value={data?.customisation_requests?.total || 0}/>
      </div>
      {data?.customisation_requests?.by_status && Object.keys(data.customisation_requests.by_status).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 text-xs" data-testid="cust-req-breakdown">
          {Object.entries(data.customisation_requests.by_status).map(([st, n]) => (
            <span key={st} className="bg-pearl border border-line px-2.5 py-1 rounded-full capitalize">{st.replace(/_/g, " ")}: <b className="text-maroon">{n}</b></span>
          ))}
        </div>
      )}

      {/* Metal sold, by purity */}
      <div className="bg-white border border-line p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Scale size={16} className="text-gold"/>
          <h2 className="font-display text-2xl text-maroon">Metal Sold — by purity</h2>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {GRAM_KEYS.map(([k, label]) => (
            <div key={k} className="border border-line p-3" data-testid={`grams-${k}`}>
              <div className="eyebrow text-[9px]">{label}</div>
              <div className="font-display text-xl text-maroon mt-1">{(grams[k] || 0).toFixed(3)}</div>
              <div className="text-[10px] text-ash">grams</div>
            </div>
          ))}
        </div>
        {gramChart.length > 0 && (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gramChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8"/>
                <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip formatter={(v) => [`${v} g`, "Grams"]}/>
                <Bar dataKey="grams" fill={GOLD} radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Revenue by person */}
      {peopleChart.length > 0 && (
        <div className="bg-white border border-line p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={16} className="text-gold"/>
            <h2 className="font-display text-2xl text-maroon">Revenue by Person</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peopleChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8"/>
                <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip formatter={(v) => [fmtINR(v), "Revenue"]}/>
                <Bar dataKey="revenue" fill={MAROON} radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Trend over time */}
      <div className="bg-white border border-line p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="font-display text-2xl text-maroon">Daily Trend</h2>
          <select className="border border-line px-2 py-1 text-sm bg-pearl" value={who}
            onChange={(e) => setWho(e.target.value)} data-testid="trend-person">
            <option value="">Me ({user?.name})</option>
            {(data?.people || []).map((p) => <option key={p.staff_id} value={p.staff_id}>{p.name}</option>)}
          </select>
        </div>
        {trend.length === 0 ? (
          <div className="text-xs text-ash py-8 text-center">No sales in this period.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8"/>
                <XAxis dataKey="date" tick={{ fontSize: 10 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip formatter={(v, n) => n === "revenue" ? [fmtINR(v), "Revenue"] : [v, "Slips"]}/>
                <Line type="monotone" dataKey="revenue" stroke={MAROON} strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="slips" stroke={GOLD} strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-person table with grams */}
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Person", "Role", "Revenue", "Orders", "Slips", "Items", "Gems", "Custom", "Schemes",
                  "24K g", "22K g", "20K g", "18K g", "Silver g"].map((h) => (
              <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(data?.people || []).map((p) => (
              <tr key={p.staff_id} className="border-b border-line/60" data-testid={`an-row-${p.staff_id}`}>
                <td className="p-3 font-medium whitespace-nowrap">{p.name}</td>
                <td className="p-3 text-xs text-ash whitespace-nowrap">{(p.role || "").replace("_", " ")}</td>
                <td className="p-3 text-maroon font-medium whitespace-nowrap">{fmtINR(p.revenue)}</td>
                <td className="p-3">{p.orders_count}</td>
                <td className="p-3">{p.slips_count}</td>
                <td className="p-3">{p.items_sold}</td>
                <td className="p-3">{p.gemstones_sold || 0}</td>
                <td className="p-3">{p.customisations}</td>
                <td className="p-3">{p.schemes_enrolled}</td>
                {["24K", "22K", "20K", "18K", "silver"].map((k) => (
                  <td key={k} className="p-3 text-xs">{(p.grams?.[k] || 0).toFixed(3)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {(data?.people || []).length === 0 && (
          <div className="p-12 text-center text-ash text-sm">No sales recorded for this period.</div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-line p-4">
    <div className="eyebrow text-[9px]">{label}</div>
    <div className="font-display text-2xl text-maroon mt-1">{value}</div>
  </div>
);
